import { useState, useEffect, useCallback } from 'react';
import { getAdminUserTotals } from '../../api/adminApi';

const cyan = '#00e5ff';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const COLUMNS = [
  { key: 'userCode',         label: 'User Code',       sortable: true },
  { key: 'name',             label: 'Name',            sortable: true },
  { key: 'totalUpgrades',    label: 'Total Upgrades',  sortable: true },
  { key: 'totalEarnings',    label: 'Total Earnings',  sortable: true },
  { key: 'totalWithdrawals', label: 'Total Withdrawals', sortable: true },
  { key: 'earningBalance',   label: 'Earning Balance', sortable: true },
  { key: 'oneWalletBalance', label: 'OneWallet Balance', sortable: true },
  { key: 'mainWalletBalance', label: 'Main Wallet Balance', sortable: true },
];

const fmtAmt = (n) => (Number(n) || 0).toFixed(2);

const TotalChip = ({ label, value, color, sub }) => (
  <div style={{ background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 10, padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ color: '#666', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    <span style={{ color, fontSize: 18, fontWeight: 800, fontFamily: 'monospace' }}>{value}</span>
    {sub && <span style={{ color: '#555', fontSize: 10 }}>{sub}</span>}
  </div>
);

const AdminUserTotals = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [inputVal, setInputVal] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [grandTotals, setGrandTotals] = useState(null);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminUserTotals({ search, page, limit: 20, sortBy, sortDir })
      .then((res) => {
        setUsers(res.data.users);
        setTotal(res.data.total);
        setPages(res.data.pages);
        setGrandTotals(res.data.grandTotals || null);
      })
      .finally(() => setLoading(false));
  }, [search, page, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(inputVal);
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  const sortArrow = (key) => sortBy === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>User Totals</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>{total} total users{search ? ' (matching search)' : ''}</div>

      {/* Grand totals across all matching users */}
      {grandTotals && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          <TotalChip label="Total Upgrades" value={grandTotals.totalUpgrades} color={cyan} sub={`${fmtAmt(grandTotals.totalUpgradeAmount)} USDT`} />
          <TotalChip label="Total Earnings" value={`$${fmtAmt(grandTotals.totalEarnings)}`} color="#66ff99" />
          <TotalChip label="Total Withdrawals" value={`$${fmtAmt(grandTotals.totalWithdrawals)}`} color="#ffaa44" />
          <TotalChip label="Earning Balance" value={`$${fmtAmt(grandTotals.earningBalance)}`} color="#66ff99" />
          <TotalChip label="OneWallet Balance" value={`$${fmtAmt(grandTotals.oneWalletBalance)}`} color="#ff88cc" />
          <TotalChip label="Main Wallet Balance" value={`$${fmtAmt(grandTotals.mainWalletBalance)}`} color="#66aaff" />
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="Search by user code or name..."
          style={{ flex: 1, minWidth: isMobile ? '100%' : 'auto', background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={() => { setInputVal(''); setSearch(''); setPage(1); }}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Clear
          </button>
        )}
      </form>

      {/* Table (scrolls horizontally on mobile) */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 900 : 'auto' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                <th style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>#</th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap', cursor: c.sortable ? 'pointer' : 'default', userSelect: 'none' }}
                  >
                    {c.label}{sortArrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLUMNS.length + 1} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={COLUMNS.length + 1} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No users found.</td></tr>
              ) : users.map((u, i) => (
                <tr key={u._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 20 + i + 1}</td>
                  <td style={{ color: cyan, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace' }}>{u.userCode}</td>
                  <td style={{ color: '#fff', fontSize: 13, fontWeight: 500, padding: '11px 14px', whiteSpace: 'nowrap' }}>{u.name}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13 }}>{u.totalUpgrades}</div>
                    <div style={{ color: '#66ff99', fontSize: 11, marginTop: 2 }}>{fmtAmt(u.totalUpgradeAmount)} USDT</div>
                  </td>
                  <td style={{ color: '#66ff99', fontSize: 13, fontWeight: 600, padding: '11px 14px' }}>{fmtAmt(u.totalEarnings)}</td>
                  <td style={{ color: '#ffaa44', fontSize: 13, fontWeight: 600, padding: '11px 14px' }}>{fmtAmt(u.totalWithdrawals)}</td>
                  <td style={{ color: '#66ff99', fontSize: 13, padding: '11px 14px' }}>{fmtAmt(u.earningBalance)}</td>
                  <td style={{ color: '#ff88cc', fontSize: 13, padding: '11px 14px' }}>{fmtAmt(u.oneWalletBalance)}</td>
                  <td style={{ color: '#66aaff', fontSize: 13, padding: '11px 14px' }}>{fmtAmt(u.mainWalletBalance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, isMobile ? 5 : pages) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
          {isMobile && pages > 5 && <span style={{ color: '#555', fontSize: 12, padding: '6px 12px' }}>... {pages}</span>}
        </div>
      )}
    </div>
  );
};

export default AdminUserTotals;
